import React, { useEffect, useMemo, useState } from 'react';
import { Button, Card, Dropdown, Icon, theme, Divider, Text } from 'components';
import errorReporter from '@app/errorReporter';
import { Checkbox, Space, Tooltip, message } from 'antd';
import styled from 'styled-components';

import { useBreakpoint } from 'hooks';
import { sendFeedbackToClients } from 'api/FeedbackController';
import { ClientConfigContact } from '@bb/domain/dist/types/AppointmentConfig';
import { sortBy } from 'lodash';
import { AnyARecord } from 'dns';
import { StyledTooltipButtonWrapper } from '../../TourEditPage/StyledComponents';

const SendToClients = ({ appointment, appointmentConfig }: any) => {
  const { md } = useBreakpoint();

  const [isDropdownVisible, setIsDropdownVisible] = useState(false);
  const [isSending, setIsSending] = useState(false);
  const [selectedContactIds, setSelectedContactIds] = useState(
    new Set<string>(),
  );

  const clientContacts: ClientConfigContact[] = useMemo(
    () =>
      sortBy(
        appointmentConfig?.contacts?.filter(({ type }: AnyARecord) =>
          ['Seller', 'Tenant', 'Landlord/Manager'].includes(type),
        ) as ClientConfigContact[],
        contact =>
          `${contact.firstName}${contact.lastName}`
            .replace(/\s/g, '')
            .toLowerCase(),
      ),
    [appointmentConfig?.contacts],
  );

  useEffect(() => {
    setSelectedContactIds(
      new Set(clientContacts.map(({ contactId }) => contactId)),
    );
  }, [clientContacts]);

  const selectedContactIdsWithEmail = useMemo(() => {
    const contactIdsWithEmail = clientContacts
      .filter(({ email }) => Boolean(email))
      .map(({ contactId }) => contactId);
    return [...selectedContactIds].filter(contactId =>
      contactIdsWithEmail.includes(contactId),
    );
  }, [selectedContactIds, clientContacts]);

  const sendFeedback = async () => {
    setIsSending(true);
    try {
      await sendFeedbackToClients({
        listingId: appointmentConfig.listingId,
        appointmentIds: [appointment.id],
        contactIds: [...selectedContactIdsWithEmail],
      });
      message.success('Feedback sent');
      setIsDropdownVisible(false);
    } catch (err) {
      message.error('Feedback could not be sent. Please try again.');
      errorReporter.error(err);
    }
    setIsSending(false);
  };

  const contactsDropdownContent = (
    <StyledDropdownCard data-cy={'feedback-manual-send-receivers'}>
      <Space direction="vertical" size="small">
        <StyledScrollable>
          <Space direction="vertical" size="small">
            {clientContacts.map(contact => (
              <Tooltip
                title={!contact.email && 'User requires email.'}
                trigger={md ? 'hover' : 'click'}
                key={contact.contactId}
                placement="topLeft"
              >
                <StyledCheckBox
                  checked={selectedContactIds.has(contact.contactId)}
                  onChange={e => {
                    const newSelection = new Set(selectedContactIds);
                    if (e.target.checked) newSelection.add(contact.contactId);
                    else newSelection.delete(contact.contactId);
                    setSelectedContactIds(newSelection);
                  }}
                  data-cy={`send-feedback-select-${contact.contactId}`}
                  disabled={!contact.email}
                >
                  {`${contact.firstName} ${contact.lastName}`},
                  <Text type="secondary">{` ${contact.type}`}</Text>
                </StyledCheckBox>
              </Tooltip>
            ))}
          </Space>
        </StyledScrollable>
        <Divider />
        <StyledDropdownFooter>
          <Tooltip
            title={clientContacts.length <= 0 && 'No Listing Contacts'}
            trigger={md ? 'hover' : 'click'}
          >
            <StyledTooltipButtonWrapper>
              <Button
                disabled={!selectedContactIdsWithEmail.length}
                loading={isSending}
                onClick={sendFeedback}
                type="primary"
                size="small"
                data-cy={'send-feedback-button'}
              >
                Send Feedback
              </Button>
            </StyledTooltipButtonWrapper>
          </Tooltip>
        </StyledDropdownFooter>
      </Space>
    </StyledDropdownCard>
  );

  return (
    <StyledContainer>
      <Dropdown
        disabled={!clientContacts.length}
        onOpenChange={setIsDropdownVisible}
        open={isDropdownVisible}
        dropdownRender={() => contactsDropdownContent}
        trigger={['click']}
        overlayStyle={{ zIndex: 1 }}
      >
        <StyledDropDownButton
          data-cy={'send-to-clients-button'}
          type="link"
          disabled={!clientContacts.length}
        >
          Send to clients
          <StyledIcon
            type="antdDownOutlined"
            width={14}
            color={!clientContacts.length ? 'gray5' : 'blue6'}
          />
        </StyledDropDownButton>
      </Dropdown>
    </StyledContainer>
  );
};

export default SendToClients;

const StyledContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
`;

const StyledIcon = styled(Icon)`
  margin-left: 5px;
`;

const StyledDropDownButton = styled(Button)`
  align-items: center;
  font-size: 14px;
  font-weight: 500;
  padding: 0;
  color: ${theme.blue6};
  text-decoration: underline;
`;

const StyledDropdownFooter = styled.div`
  display: flex;
  justify-content: center;

  ${Button} {
    border-radius: 2px;
  }
`;

const StyledDropdownCard = styled(Card)`
  padding: 5px;
  ${Divider} {
    margin: 0;
  }
`;

const StyledCheckBox = styled(Checkbox)`
  && .ant-checkbox-disabled + span {
    color: #262626;
  }
`;

const StyledScrollable = styled.div`
  max-height: 35vh;
  overflow-y: auto;

  // magical fix for scrollbar flickering when selecting the last item.
  padding-bottom: 2px;
  margin-bottom: -2px;
`;
